import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { PermissionsMap } from "../../domain/entities/PermissionEntity";
import {
  ModuleCode,
  ActionCode,
  ACTION_CODES,
} from "../../constants/permissions";
import { checkApprovedReview } from "../../utils/reviewWorkflow";
import { usePermissions } from "./usePermissions";

export interface UseDocumentReviewDataOptions<T> {
  moduleCode: ModuleCode;
  entityType: string;
  documentId: string | null;
  fetchDocument: (id: string) => Promise<T>;
  isPostedToZoho: (document: T) => boolean;
  initialDocument?: T | null;
}

export interface DocumentReviewDataState {
  // Loading states
  isInitialDataLoading: boolean; // Permissions + Document
  isReviewDataLoading: boolean; // Review statuses
  isDataLoading: boolean; // Combined: true if either above is true

  // Permissions
  permissions: PermissionsMap | null;

  // Document
  document: any | null;
  isPostedToZoho: boolean;

  // Permission flags (computed after permissions loaded)
  canEdit: boolean;
  canUpdateAfterPush: boolean;
  canPushToZoho: boolean;
  canCreate: boolean;

  canOverrideGLPermanent: boolean;
  canOverrideGLOnce: boolean;
  canReject: boolean;
  canMarkAsDuplicate: boolean;
  canDelete: boolean;
  canCancelWriteOff: boolean;

  // Review requirements (computed after permissions loaded)
  needsReviewForEdit: boolean;
  needsReviewForUpdateAfterPush: boolean;
  needsReviewForPushToZoho: boolean;
  needsReviewForCreate: boolean;
  needsReviewForOverrideGLPermanent: boolean;
  needsReviewForReject: boolean;
  needsReviewForDuplicate: boolean;
  needsReviewForDelete: boolean;
  needsReviewForCancelWriteOff: boolean;

  // Review statuses (fetched conditionally) - Dynamic map for all actions
  // Key: action_code, Value: boolean | null (null = not checked, true = approved, false = not approved)
  reviewStatuses: Record<string, boolean | null>;

  // Helper function to get review status for any action
  getReviewStatus: (actionCode: ActionCode) => boolean | null;

  // Helper function to check if action has approved review (returns true if no review needed)
  hasApprovedReview: (actionCode: ActionCode) => boolean;

  // Legacy review statuses (for backward compatibility)
  hasApprovedUpdateAfterPushReview: boolean | null;
  hasApprovedPushToZohoReview: boolean | null;
  hasApprovedOverrideGLPermanentReview: boolean | null;

  // Final computed flags (computed after ALL data loaded)
  canActuallyEdit: boolean;
  canSaveAndGoBack: boolean;
  canApprove: boolean;
  canEditLineItem: boolean; // Includes override_gl_permanent review check

  // Errors
  error: Error | null;
}

/**
 * Generic hook to fetch document review data (permissions + document + review statuses)
 * Fetches everything in parallel using Promise.all() and conditionally checks review statuses
 *
 * @param options - Configuration options
 * @returns Document review data state with all permission flags and review statuses
 */
export const useDocumentReviewData = <T extends any>(
  options: UseDocumentReviewDataOptions<T>
): DocumentReviewDataState => {
  const {
    moduleCode,
    entityType,
    documentId,
    fetchDocument,
    isPostedToZoho: checkIsPostedToZoho,
    initialDocument,
  } = options;

  // Use permissions from context instead of fetching directly
  const { permissions: contextPermissions, loading: permissionsLoading } =
    usePermissions();

  // Loading states
  const [isInitialDataLoading, setIsInitialDataLoading] =
    useState<boolean>(true);
  const [isReviewDataLoading, setIsReviewDataLoading] =
    useState<boolean>(false);

  // Data states
  const [permissions, setPermissions] = useState<PermissionsMap | null>(null);
  const [document, setDocument] = useState<T | null>(initialDocument || null);
  const [isPostedToZoho, setIsPostedToZoho] = useState<boolean>(false);

  // Permission flags
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [canUpdateAfterPush, setCanUpdateAfterPush] = useState<boolean>(false);
  const [canPushToZoho, setCanPushToZoho] = useState<boolean>(false);
  const [canCreate, setCanCreate] = useState<boolean>(false);

  const [canOverrideGLPermanent, setCanOverrideGLPermanent] =
    useState<boolean>(false);
  const [canOverrideGLOnce, setCanOverrideGLOnce] = useState<boolean>(false);
  const [canReject, setCanReject] = useState<boolean>(false);
  const [canMarkAsDuplicate, setCanMarkAsDuplicate] = useState<boolean>(false);
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [canCancelWriteOff, setCanCancelWriteOff] = useState<boolean>(false);

  // Review requirements
  const [needsReviewForEdit, setNeedsReviewForEdit] = useState<boolean>(false);
  const [needsReviewForUpdateAfterPush, setNeedsReviewForUpdateAfterPush] =
    useState<boolean>(false);
  const [needsReviewForPushToZoho, setNeedsReviewForPushToZoho] =
    useState<boolean>(false);
  const [needsReviewForCreate, setNeedsReviewForCreate] =
    useState<boolean>(false);
  const [
    needsReviewForOverrideGLPermanent,
    setNeedsReviewForOverrideGLPermanent,
  ] = useState<boolean>(false);
  const [needsReviewForReject, setNeedsReviewForReject] =
    useState<boolean>(false);
  const [needsReviewForDuplicate, setNeedsReviewForDuplicate] =
    useState<boolean>(false);
  const [needsReviewForDelete, setNeedsReviewForDelete] =
    useState<boolean>(false);
  const [needsReviewForCancelWriteOff, setNeedsReviewForCancelWriteOff] =
    useState<boolean>(false);

  // Review statuses - Dynamic map for all actions
  const [reviewStatuses, setReviewStatuses] = useState<
    Record<string, boolean | null>
  >({});

  // Error state
  const [error, setError] = useState<Error | null>(null);

  // Combined loading state
  const isDataLoading = isInitialDataLoading || isReviewDataLoading;

  // Use refs to store stable function references
  const fetchDocumentRef = useRef(fetchDocument);
  const checkIsPostedToZohoRef = useRef(checkIsPostedToZoho);

  // Update refs immediately when functions change (not in useEffect to avoid extra renders)
  // This ensures refs always point to the latest functions without triggering re-fetches
  fetchDocumentRef.current = fetchDocument;
  checkIsPostedToZohoRef.current = checkIsPostedToZoho;

  // Main data fetching effect - now waits for permissions from context
  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounts

    const fetchAllData = async () => {
      // CRITICAL: Wait for permissions to be loaded from context
      if (permissionsLoading) {
        setIsInitialDataLoading(true);
        return; // Don't proceed until permissions are loaded
      }

      // If no permissions available yet, wait
      if (!contextPermissions) {
        setIsInitialDataLoading(true);
        return;
      }

      if (!documentId) {
        // No document ID, can't fetch document, but we have permissions
        if (isMounted) {
          setPermissions(contextPermissions); // Set permissions from context
          setIsInitialDataLoading(false);
          setIsReviewDataLoading(false);
        }
        return;
      }

      try {
        // ============================================
        // STEP 1: Use permissions from context + Fetch Document
        // ============================================
        if (isMounted) {
          setIsInitialDataLoading(true);
          setError(null);
        }

        // Use permissions from context (already loaded)
        const permissionsResult = contextPermissions;

        // Only fetch document (permissions already available from context)
        const documentResult = await fetchDocumentRef.current(documentId);

        if (!isMounted) return;

        // Set permissions and document
        setPermissions(permissionsResult);
        setDocument(documentResult);

        // Determine if posted to Zoho
        const isPosted = checkIsPostedToZohoRef.current(documentResult);
        setIsPostedToZoho(isPosted);

        // ============================================
        // STEP 2: Compute permission flags from loaded permissions
        // ============================================
        const modulePermissions = permissionsResult[moduleCode] || {};

        // Get permission info based on posted status
        const editInfo = isPosted
          ? modulePermissions[ACTION_CODES.UPDATE_AFTER_PUSH]
          : modulePermissions[ACTION_CODES.EDIT];

        const updateAfterPushInfo = modulePermissions[
          ACTION_CODES.UPDATE_AFTER_PUSH
        ] || {
          granted: false,
          requires_review: false,
        };
        const pushToZohoInfo = modulePermissions[ACTION_CODES.PUSH_TO_ZOHO] || {
          granted: false,
          requires_review: false,
        };
        const overrideGLPermInfo = modulePermissions[
          ACTION_CODES.OVERRIDE_GL_PERMANENT
        ] || {
          granted: false,
          requires_review: false,
        };
        const overrideGLOnceInfo = modulePermissions[
          ACTION_CODES.OVERRIDE_GL_ONCE
        ] || {
          granted: false,
          requires_review: false,
        };
        const rejectInfo = modulePermissions[ACTION_CODES.REJECT] || {
          granted: false,
          requires_review: false,
        };
        const duplicateInfo = modulePermissions[ACTION_CODES.DUPLICATE] || {
          granted: false,
          requires_review: false,
        };
        const createInfo = modulePermissions[ACTION_CODES.CREATE] || {
          granted: false,
          requires_review: false,
        };
        const deleteInfo =
          modulePermissions[ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO] || {
            granted: false,
            requires_review: false,
          };
        const cancelWriteOffInfo =
          modulePermissions[ACTION_CODES.CANCEL_WRITE_OFF] || {
            granted: false,
            requires_review: false,
          };

        // Set permission flags
        setCanEdit(editInfo?.granted ?? false);
        setCanUpdateAfterPush(updateAfterPushInfo.granted);
        setCanPushToZoho(pushToZohoInfo.granted);
        setCanCreate(createInfo.granted);

        setCanOverrideGLPermanent(overrideGLPermInfo.granted);
        setCanOverrideGLOnce(overrideGLOnceInfo.granted);
        setCanReject(rejectInfo.granted);
        setCanMarkAsDuplicate(duplicateInfo.granted);
        setCanDelete(deleteInfo.granted);
        setCanCancelWriteOff(cancelWriteOffInfo.granted);

        // Set review requirements
        setNeedsReviewForEdit(editInfo?.requires_review ?? false);
        setNeedsReviewForUpdateAfterPush(updateAfterPushInfo.requires_review);
        setNeedsReviewForPushToZoho(pushToZohoInfo.requires_review);
        setNeedsReviewForCreate(createInfo.requires_review);
        setNeedsReviewForOverrideGLPermanent(
          overrideGLPermInfo.requires_review
        );
        setNeedsReviewForReject(rejectInfo.requires_review);
        setNeedsReviewForDuplicate(duplicateInfo.requires_review);
        setNeedsReviewForDelete(deleteInfo.requires_review);
        setNeedsReviewForCancelWriteOff(cancelWriteOffInfo.requires_review);

        setIsInitialDataLoading(false);

        // ============================================
        // STEP 3: Fetch review statuses (DYNAMIC - for ALL actions that require review)
        // ============================================
        setIsReviewDataLoading(true);

        // Dynamically check review status for ALL actions that:
        // 1. Have permission granted
        // 2. Require review
        const reviewPromises: Promise<{
          actionCode: string;
          approved: boolean;
        }>[] = [];

        // Iterate through all actions in the module permissions
        Object.keys(modulePermissions).forEach((actionCode) => {
          const actionInfo = modulePermissions[actionCode];

          // Check if this action requires review and user has permission
          if (actionInfo.granted && actionInfo.requires_review) {
            // For UPDATE action: only check if document is NOT posted
            // For UPDATE_AFTER_PUSH action: only check if document IS posted
            // For other actions: always check if granted and requires review

            let shouldCheckReview = false;

            if (actionCode === ACTION_CODES.EDIT) {
              // Only check edit review if document is NOT posted
              shouldCheckReview = !isPosted;
            } else if (actionCode === ACTION_CODES.UPDATE_AFTER_PUSH) {
              // Only check update_after_push review if document IS posted
              shouldCheckReview = isPosted;
            } else if (actionCode === ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO) {
              // Only check delete_after_push_to_zoho review if document IS posted to Zoho
              shouldCheckReview = isPosted;
            } else if (actionCode === ACTION_CODES.EDIT) {
              // Only check edit review if document is NOT posted
              shouldCheckReview = !isPosted;
            } else {
              // For all other actions (push_to_zoho, override_gl_permanent, etc.)
              // Check review if granted and requires review
              shouldCheckReview = true;
            }

            if (shouldCheckReview) {
              reviewPromises.push(
                checkApprovedReview({
                  entity_type: entityType,
                  entity_id: parseInt(documentId),
                  action_code: actionCode,
                })
                  .then((result) => ({
                    actionCode,
                    approved: result.data.approved,
                  }))
                  .catch(() => ({
                    actionCode,
                    approved: false,
                  }))
              );
            }
          }
        });

        // Execute all review checks in parallel
        if (reviewPromises.length > 0) {
          const reviewResults = await Promise.all(reviewPromises);

          if (!isMounted) return;

          // Update review statuses map dynamically
          const newReviewStatuses: Record<string, boolean | null> = {};
          reviewResults.forEach((result) => {
            newReviewStatuses[result.actionCode] = result.approved;
          });

          setReviewStatuses(newReviewStatuses);
        } else {
          // No reviews needed, initialize empty map
          setReviewStatuses({});
        }

        setIsReviewDataLoading(false);
      } catch (err) {
        console.error("Error fetching document review data:", err);
        if (isMounted) {
          setIsInitialDataLoading(false);
          setIsReviewDataLoading(false);
          setError(
            err instanceof Error ? err : new Error("Failed to load data")
          );
        }
      }
    };

    fetchAllData();

    return () => {
      isMounted = false; // Cleanup
    };
    // Add contextPermissions and permissionsLoading to dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentId,
    moduleCode,
    entityType,
    contextPermissions,
    permissionsLoading,
  ]);

  // ============================================
  // STEP 4: Helper functions to get review status dynamically
  // ============================================

  // Helper function to get review status for any action
  const getReviewStatus = useCallback(
    (actionCode: ActionCode): boolean | null => {
      return reviewStatuses[actionCode] ?? null;
    },
    [reviewStatuses]
  );

  // Helper function to check if action has approved review
  // Returns true if no review needed, false if review needed but not approved, true if approved
  const hasApprovedReview = useCallback(
    (actionCode: ActionCode): boolean => {
      const status = reviewStatuses[actionCode];
      // If status is null, it means review wasn't checked (no review needed or not applicable)
      // Return true in that case (assume approved if not checked)
      if (status === null) return true;
      return status === true;
    },
    [reviewStatuses]
  );

  // ============================================
  // STEP 5: Compute final flags with useMemo (using dynamic review statuses)
  // ============================================

  // Legacy review statuses (for backward compatibility)
  const hasApprovedUpdateAfterPushReview = useMemo(
    () => reviewStatuses[ACTION_CODES.UPDATE_AFTER_PUSH] ?? null,
    [reviewStatuses]
  );
  const hasApprovedPushToZohoReview = useMemo(
    () => reviewStatuses[ACTION_CODES.PUSH_TO_ZOHO] ?? null,
    [reviewStatuses]
  );
  const hasApprovedOverrideGLPermanentReview = useMemo(
    () => reviewStatuses[ACTION_CODES.OVERRIDE_GL_PERMANENT] ?? null,
    [reviewStatuses]
  );

  const canActuallyEdit = useMemo(() => {
    if (isDataLoading) return false;

    if (isPostedToZoho) {
      return (
        canUpdateAfterPush &&
        (!needsReviewForUpdateAfterPush ||
          hasApprovedReview(ACTION_CODES.UPDATE_AFTER_PUSH))
      );
    } else {
      return canEdit;
    }
  }, [
    isDataLoading,
    isPostedToZoho,
    canUpdateAfterPush,
    canEdit,
    needsReviewForUpdateAfterPush,
    hasApprovedReview,
  ]);

  const canSaveAndGoBack = useMemo(() => {
    if (isDataLoading) return false;
    if (isPostedToZoho) return false; // Never show for posted docs

    // Hide "Save and Go Back" if ANY action requires review (and user has that permission)
    const anyActionRequiresReview =
      (canEdit && needsReviewForEdit) ||
      (canPushToZoho && needsReviewForPushToZoho) ||
      (canUpdateAfterPush && needsReviewForUpdateAfterPush) || // Shouldn't happen for non-posted, but defensive
      (canOverrideGLPermanent && needsReviewForOverrideGLPermanent);

    return canEdit && !anyActionRequiresReview;
  }, [
    isDataLoading,
    isPostedToZoho,
    canEdit,
    needsReviewForEdit,
    canPushToZoho,
    needsReviewForPushToZoho,
    canUpdateAfterPush,
    needsReviewForUpdateAfterPush,
    canOverrideGLPermanent,
    needsReviewForOverrideGLPermanent,
  ]);

  const canApprove = useMemo(() => {
    if (isDataLoading) return false;

    // Only check permissions - review status will be checked in handleDocumentApproval
    // This allows the button to be enabled so users can click it and trigger the review modal if needed
    if (isPostedToZoho) {
      return canUpdateAfterPush;
    } else {
      return canPushToZoho;
    }
  }, [isDataLoading, isPostedToZoho, canUpdateAfterPush, canPushToZoho]);

  const canEditLineItem = useMemo(() => {
    if (isDataLoading) return false;

    // Can edit if user has update permission
    const canEdit = canActuallyEdit;

    // BUT also check override_gl_permanent review if needed
    if (canOverrideGLPermanent && needsReviewForOverrideGLPermanent) {
      return canEdit && hasApprovedReview(ACTION_CODES.OVERRIDE_GL_PERMANENT);
    }

    return canEdit;
  }, [
    isDataLoading,
    canActuallyEdit,
    canOverrideGLPermanent,
    needsReviewForOverrideGLPermanent,
    hasApprovedReview,
  ]);

  return {
    // Loading states
    isInitialDataLoading,
    isReviewDataLoading,
    isDataLoading,

    // Data
    permissions,
    document,
    isPostedToZoho,

    // Permission flags
    canEdit,
    canUpdateAfterPush,
    canPushToZoho,
    canCreate,

    canOverrideGLPermanent,
    canOverrideGLOnce,
    canReject,
    canMarkAsDuplicate,
    canDelete,
    canCancelWriteOff,

    // Review requirements
    needsReviewForEdit,
    needsReviewForUpdateAfterPush,
    needsReviewForPushToZoho,
    needsReviewForCreate,
    needsReviewForOverrideGLPermanent,
    needsReviewForReject,
    needsReviewForDuplicate,
    needsReviewForDelete,
    needsReviewForCancelWriteOff,

    // Review statuses - Dynamic map for all actions
    reviewStatuses,
    getReviewStatus,
    hasApprovedReview,

    // Legacy review statuses (for backward compatibility)
    hasApprovedUpdateAfterPushReview,
    hasApprovedPushToZohoReview,
    hasApprovedOverrideGLPermanentReview,

    // Final computed flags
    canActuallyEdit,
    canSaveAndGoBack,
    canApprove,
    canEditLineItem,

    // Errors
    error,
  };
};
